import { Text } from "@/components/podkit/typography/Text";
import {
    type PlainWorkflow,
    type PlainWorkflowExecution,
    useGetMostRecentExecution,
} from "@/queries/automation-queries";
import { workflowName, isWorkflowDeleting, isWorkflowRunning, triggerName, triggerIcon } from "@/utils/automations";
import { type FC, useState } from "react";
import { AutomationPropertiesHeader } from "@/routes/automations/AutomationPropertiesHeader";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { WorkflowActionsDropdown } from "@/components/automations/AutomationActionsDropdown.tsx";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { SubjectDisplay } from "@/components/SubjectDisplay";
import { AutomationGroupAccess } from "@/components/automations/AutomationGroupAccess";
import { AutomationTriggerIconWithTooltip } from "@/components/automations/AutomationTriggerIcon";
import { Pill } from "@/components/podkit/status/Pill";
import { timeAgo } from "@/format/time";
import { Timestamp } from "@bufbuild/protobuf";
import { WorkflowExecutionPhase } from "gitpod-next-api/gitpod/v1/workflow_pb";
import { Tooltip } from "@/components/Tooltip";
import { WorkflowExecutionStatusIcon } from "@/components/automations/AutomationExecutionStatusIcon";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";

type WorkflowCardProps = {
    workflow: PlainWorkflow;
    readonly?: boolean;
    /** "standalone" renders with border/rounded; "list-item" removes border for use in a bordered container */
    variant?: "standalone" | "list-item";
} & PropsWithClassName;

export const AutomationCard: FC<WorkflowCardProps> = ({
    workflow,
    className,
    readonly = false,
    variant = "standalone",
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { isMobileViewport } = useIsMobileViewport();

    const { data: mostRecentExecution } = useGetMostRecentExecution({
        enabled: true,
        workflowId: workflow.id,
    });

    const hasDescription = !!workflow.metadata?.description;
    const isDeleting = isWorkflowDeleting(workflow);
    const isRunning = mostRecentExecution ? isWorkflowRunning(mostRecentExecution) : false;
    const showActionsMenu = !readonly;
    const firstTrigger = workflow.spec?.triggers?.[0];
    const executionExecutor = mostRecentExecution?.metadata?.executor;
    const TriggerIcon = triggerIcon(firstTrigger);

    const actionsDropdown = showActionsMenu && (
        <WorkflowActionsDropdown
            workflow={workflow}
            showRunAction={true}
            buttonSize={"sm"}
            buttonVariant={variant === "list-item" ? "ghost" : "outline"}
            open={isDropdownOpen}
            onOpenChange={setIsDropdownOpen}
        />
    );

    return (
        <div
            data-active={isDropdownOpen}
            className={cn(
                "flex gap-3 p-4",
                variant === "standalone" && "rounded-xl border border-border-base",
                variant === "standalone" && !readonly && "data-[active=true]:shadow-sm",
                variant === "list-item" &&
                    !readonly &&
                    "hover:bg-surface-button-clear-accent data-[active=true]:bg-surface-button-clear-accent",
                className,
            )}
        >
            {/* Desktop: trigger icon */}
            {!isMobileViewport && (
                <div>
                    <AutomationTriggerIconWithTooltip trigger={firstTrigger} size={40} />
                </div>
            )}

            {/* Main content */}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                {/* Mobile: top row with trigger badge, shared groups, and menu */}
                {isMobileViewport && (
                    <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1 rounded-full bg-surface-muted px-1.5 py-0.5 text-xs text-content-strong">
                            <TriggerIcon size={16} />
                            {triggerName(firstTrigger)}
                        </span>
                        <div className="flex items-center gap-1">
                            <AutomationGroupAccess workflowId={workflow.id} maxVisible={3} />
                            {actionsDropdown}
                        </div>
                    </div>
                )}

                <div>
                    <div className="flex items-center gap-2">
                        <Tooltip
                            usePortal
                            side="bottom"
                            align="start"
                            content={
                                <div className="flex max-w-xs flex-col gap-1">
                                    <span className="font-medium">{workflowName(workflow)}</span>
                                    {hasDescription && (
                                        <span className="text-content-primary-inverted">
                                            {workflow.metadata?.description}
                                        </span>
                                    )}
                                </div>
                            }
                        >
                            <Text className="text-base font-medium leading-none text-content-primary">
                                {workflowName(workflow)}
                            </Text>
                        </Tooltip>
                        {isRunning && (
                            <Pill size="sm" variant="brand">
                                Running
                            </Pill>
                        )}
                    </div>
                    {variant === "standalone" &&
                        (hasDescription ? (
                            <Text className="text-base text-content-strong">{workflow.metadata?.description}</Text>
                        ) : (
                            <div className="mt-2">
                                <AutomationPropertiesHeader workflow={workflow} editable={false} />
                            </div>
                        ))}
                </div>

                {/* Execution meta - wraps on mobile */}
                <div className="flex flex-wrap items-center gap-1 text-base text-content-strong">
                    {isDeleting ? (
                        <>
                            <IconSpinner size="sm" className="shrink-0 animate-spin" />
                            <span>Deleting</span>
                        </>
                    ) : mostRecentExecution ? (
                        <>
                            <WorkflowExecutionStatusIcon execution={mostRecentExecution} />
                            <span>
                                {isRunning ? <span>Execution</span> : <span>Last execution</span>}{" "}
                                {getExecutionStatusText(mostRecentExecution)}
                            </span>
                            {!isMobileViewport && <span>by</span>}
                            <span className="font-medium">
                                <SubjectDisplay subject={executionExecutor} avatarSize={16} />
                            </span>
                        </>
                    ) : (
                        <span className="text-content-inactive">No executions yet</span>
                    )}
                </div>
            </div>

            {/* Desktop: right side actions */}
            {!isMobileViewport && (
                <div className="flex shrink-0 items-center gap-1 self-start">
                    <AutomationGroupAccess workflowId={workflow.id} maxVisible={3} />
                    {actionsDropdown}
                </div>
            )}
        </div>
    );
};

function getExecutionStatusText(execution: PlainWorkflowExecution): string {
    const phase = execution.status?.phase;
    const startedAt = execution.metadata?.startedAt;
    const finishedAt = execution.metadata?.finishedAt;

    switch (phase) {
        case WorkflowExecutionPhase.COMPLETED:
            return finishedAt ? `finished ${timeAgo(new Timestamp(finishedAt).toDate())}` : "finished";
        case WorkflowExecutionPhase.RUNNING:
            return startedAt ? `started ${timeAgo(new Timestamp(startedAt).toDate())}` : "started";
        case WorkflowExecutionPhase.PENDING:
            return "pending";
        case WorkflowExecutionPhase.STOPPED:
            return finishedAt ? `stopped ${timeAgo(new Timestamp(finishedAt).toDate())}` : "stopped";
        case WorkflowExecutionPhase.STOPPING:
            return "stopping";
        case WorkflowExecutionPhase.DELETED:
            return "deleted";
        case WorkflowExecutionPhase.DELETING:
            return "deleting";
        default:
            return "";
    }
}
